import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/context/AuthContext';
import { supabase, Conference } from '@/integrations/supabase/client';
import SpeakersAdmin from '@/components/admin/SpeakersAdmin';
import ScheduleAdmin from '@/components/admin/ScheduleAdmin';
import TypeformIntegration from '@/components/admin/TypeformIntegration';
import VenueAdmin from '@/components/admin/VenueAdmin';
import TicketsAdmin from '@/components/admin/TicketsAdmin';
import ContactAdmin from '@/components/admin/ContactAdmin';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import PageHeader from '@/components/PageHeader';
import NewsletterSubscribers from '@/components/admin/NewsletterSubscribers';

const Admin = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("speakers");
  const [searchParams] = useSearchParams();
  const [conferenceName, setConferenceName] = useState<string>('AI Summit');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [loadingConference, setLoadingConference] = useState(false);
  const { toast } = useToast();
  const [conferenceId, setConferenceId] = useState<string | undefined>(undefined);

  const webhookUrl = `${window.location.origin}/api/typeform-webhook${searchParams.get('conference') ? `?conference=${searchParams.get('conference')}` : ''}`;

  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthenticated || !user) return;
      
      try {
        setLoadingConference(true);
        
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_organizer')
          .eq('id', user.id)
          .single();
        
        if (profileError) throw profileError;
        
        if (searchParams.get('conference')) {
          const conferenceIdParam = searchParams.get('conference') || undefined;
          setConferenceId(conferenceIdParam);
          
          const { data: conferenceData, error: conferenceError } = await supabase
            .from('conferences')
            .select('name, owner_id')
            .eq('id', conferenceIdParam)
            .single();
          
          if (conferenceError) throw conferenceError;
          
          if (conferenceData.owner_id === user.id) {
            setIsOrganizer(true);
            setConferenceName(conferenceData.name);
          } else {
            setIsOrganizer(false);
            toast({
              title: "Access Denied",
              description: "You don't have permission to access this conference.",
              variant: "destructive",
            });
          }
        } else {
          setIsOrganizer(!!profileData?.is_organizer);
        }
      } catch (error: any) {
        console.error('Error checking organizer status:', error);
        toast({
          title: "Error",
          description: "Failed to verify your access permissions.",
          variant: "destructive",
        });
        setIsOrganizer(false);
      } finally {
        setLoadingConference(false);
      }
    };
    
    checkAccess();
  }, [isAuthenticated, user, searchParams, toast]);

  if (loading || loadingConference) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }
  
  if (!isOrganizer) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-black to-indigo-950">
        <Header />
        <PageHeader 
          title="Access Denied" 
          subtitle="You don't have organizer permissions to access the admin dashboard."
        />
        <main className="flex-grow pt-28 pb-20 flex items-center justify-center">
          <div className="max-w-md w-full bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-purple-500/20 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
            <p className="text-gray-300 mb-6">
              You don't have organizer permissions to access the admin dashboard.
            </p>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => window.location.href = '/create-conference'}
            >
              Create Your Own Conference
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-black to-indigo-950">
      <Header />
      <PageHeader 
        title="Admin Dashboard" 
        subtitle="Manage your conference content and settings"
      />
      <main className="flex-grow pt-28 pb-20">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {conferenceName} Admin
            </h1>
            
            {!searchParams.get('conference') && (
              <Button 
                className="mt-4 md:mt-0 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => window.location.href = '/create-conference'}
              >
                Create New Conference
              </Button>
            )}
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-8 mb-8 bg-white/10">
              <TabsTrigger 
                value="speakers" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Speakers
              </TabsTrigger>
              <TabsTrigger 
                value="schedule" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Schedule
              </TabsTrigger>
              <TabsTrigger 
                value="venue" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Venue
              </TabsTrigger>
              <TabsTrigger 
                value="tickets" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Tickets
              </TabsTrigger>
              <TabsTrigger 
                value="contact" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Contact
              </TabsTrigger>
              <TabsTrigger 
                value="integrations" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Integrations
              </TabsTrigger>
              <TabsTrigger 
                value="preview" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Preview
              </TabsTrigger>
              <TabsTrigger 
                value="subscribers" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Subscribers
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="speakers" className="mt-0">
              <SpeakersAdmin conferenceId={conferenceId} />
            </TabsContent>
            
            <TabsContent value="schedule" className="mt-0">
              <ScheduleAdmin />
            </TabsContent>

            <TabsContent value="venue" className="mt-0">
              <VenueAdmin conferenceId={conferenceId} />
            </TabsContent>

            <TabsContent value="tickets" className="mt-0">
              <TicketsAdmin conferenceId={conferenceId} />
            </TabsContent>
            
            <TabsContent value="contact" className="mt-0">
              <ContactAdmin conferenceId={conferenceId || ""} />
            </TabsContent>

            <TabsContent value="integrations" className="mt-0">
              <TypeformIntegration webhookUrl={webhookUrl} />
            </TabsContent>

            <TabsContent value="preview" className="mt-0">
              <div className="bg-white/5 border border-purple-500/20 backdrop-blur-sm p-6 rounded-lg">
                <h2 className="text-2xl font-bold text-white mb-6">Preview Conference</h2>
                <p className="text-gray-300 mb-6">View your conference website as attendees will see it.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="border-purple-500/50 hover:bg-purple-500/20"
                    onClick={() => window.open('/', '_blank')}
                  >
                    Home Page
                  </Button>
                  <Button
                    variant="outline"
                    className="border-purple-500/50 hover:bg-purple-500/20"
                    onClick={() => window.open('/venue', '_blank')}
                  >
                    Venue Page
                  </Button>
                  <Button
                    variant="outline"
                    className="border-purple-500/50 hover:bg-purple-500/20"
                    onClick={() => window.open('/contact', '_blank')}
                  >
                    Contact Page
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="subscribers" className="mt-0">
              <NewsletterSubscribers />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Admin;
